import { useMemo } from 'react';
import type { CardItem } from './types';

type VisibleCard<T> = {
  index: number;
  item: T;
  stackPosition: number;
};

type UseVisibleCardsParams<T extends CardItem> = {
  activeIndex: number;
  data: T[];
  visibleCardsCount: number;
};

export const useVisibleCards = <T extends CardItem>({
  activeIndex,
  data,
  visibleCardsCount,
}: UseVisibleCardsParams<T>): VisibleCard<T>[] => {
  return useMemo(() => {
    const renderRange = visibleCardsCount + 1;

    const leftCards = Array.from({ length: renderRange }, (_, i) => renderRange - i)
      .map((offset) => {
        const itemIndex = activeIndex - offset;
        const item = data[itemIndex];
        return item ? { index: itemIndex, item, stackPosition: -offset } : null;
      })
      .filter((card): card is VisibleCard<T> => card !== null);

    const activeItem = data[activeIndex];
    const activeCard = activeItem ? [{ index: activeIndex, item: activeItem, stackPosition: 0 }] : [];

    const rightCards = Array.from({ length: renderRange }, (_, i) => i + 1)
      .filter((offset) => activeIndex + offset < data.length)
      .map((offset) => {
        const item = data[activeIndex + offset];
        return item ? { index: activeIndex + offset, item, stackPosition: offset } : null;
      })
      .filter((card): card is VisibleCard<T> => card !== null);

    return [...leftCards, ...activeCard, ...rightCards];
  }, [activeIndex, data, visibleCardsCount]);
};
